import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Card,
  CardActionArea,
  Chip,
  Grid,
  IconButton,
  Link,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tools } from '../config/tools';
import { pagePath } from '../seo/seoData';
import { toolAccent } from '../theme';
import { recordRecent, useFavorites } from '../hooks/useToolPrefs';

interface ToolLayoutProps {
  toolId: string;
  lang: string;
  children: React.ReactNode;
}

interface FaqItem {
  q: string;
  a: string;
}

const MAX_RELATED = 6;

// Shared page frame for every tool: breadcrumb, H1, the tool itself, then
// crawlable how-to / FAQ content and internal links to the other tools.
const ToolLayout: React.FC<ToolLayoutProps> = ({ toolId, lang, children }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isFavorite, toggleFavorite } = useFavorites();
  const tool = tools.find((item) => item.id === toolId)!;
  const Icon = tool.icon;
  const accent = toolAccent(tool.color, theme.palette.mode);
  const favorite = isFavorite(toolId);
  const favoriteLabel = favorite ? t('layout.removeFavorite') : t('layout.addFavorite');

  React.useEffect(() => {
    recordRecent(toolId);
  }, [toolId]);

  const steps = t(`content.${toolId}.steps`, { returnObjects: true }) as string[];
  const faq = t(`content.${toolId}.faq`, { returnObjects: true }) as FaqItem[];
  // Tools from the same category first, then fill up with the rest.
  const related = [
    ...tools.filter((item) => item.id !== toolId && item.category === tool.category),
    ...tools.filter((item) => item.category !== tool.category),
  ].slice(0, MAX_RELATED);

  return (
    <Box component="article" sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
      <Breadcrumbs sx={{ mb: 2, fontSize: 14 }}>
        <Link component={RouterLink} to={pagePath(lang)} underline="hover" color="text.secondary">
          {t('common.appName')}
        </Link>
        <Typography color="text.primary" fontSize={14}>
          {t(`tools.${toolId}.title`)}
        </Typography>
      </Breadcrumbs>

      <Box component="header" sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 52,
            height: 52,
            borderRadius: 3,
            flexShrink: 0,
            backgroundColor: `${tool.color}1f`,
            color: accent,
          }}
        >
          <Icon fontSize="large" />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, fontSize: { xs: 26, md: 34 } }}>
            {t(`tools.${toolId}.title`)}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {t(`tools.${toolId}.description`)}
          </Typography>
        </Box>
        <Tooltip title={favoriteLabel}>
          <IconButton onClick={() => toggleFavorite(toolId)} aria-label={favoriteLabel} aria-pressed={favorite}>
            {favorite ? <StarIcon sx={{ color: 'warning.main' }} /> : <StarBorderIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box component="section">{children}</Box>

      <Chip
        icon={<LockOutlinedIcon />}
        label={t('layout.privacy')}
        variant="outlined"
        color="success"
        sx={{ mt: 3, height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal' } }}
      />

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={5}>
          <Paper component="section" sx={{ p: 3, height: '100%' }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
              {t('layout.howTo')}
            </Typography>
            <Box component="ol" sx={{ m: 0, pl: 0, listStyle: 'none', counterReset: 'step' }}>
              {steps.map((step, index) => (
                <Box component="li" key={index} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 13,
                      fontWeight: 700,
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography color="text.secondary">{step}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Box component="section">
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
              {t('layout.faq')}
            </Typography>
            {faq.map((item) => (
              <Accordion key={item.q} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography component="h3" fontWeight={600}>
                    {item.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary">{item.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Grid>
      </Grid>

      <Box component="nav" sx={{ mt: 5 }} aria-label={t('layout.related')}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
          {t('layout.related')}
        </Typography>
        <Grid container spacing={2}>
          {related.map((item) => {
            const RelatedIcon = item.icon;
            return (
              <Grid item xs={6} sm={4} md={2} key={item.id}>
                <Card sx={{ height: '100%' }}>
                  <CardActionArea
                    component={RouterLink}
                    to={pagePath(lang, item.slug)}
                    sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'center' }}
                  >
                    <RelatedIcon sx={{ color: toolAccent(item.color, theme.palette.mode) }} />
                    <Typography variant="body2" fontWeight={600}>
                      {t(`tools.${item.id}.shortTitle`)}
                    </Typography>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};

export default ToolLayout;
